import { IconButton } from '@mui/material';
import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide, { SlideProps } from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import { Theme as MuiTheme, SxProps } from '@mui/material/styles';
import { ActionsContainer, BreakpointTemplate } from 'klayowebshared';
import React, { useContext } from 'react';
import { AppContext } from '../../common/AppContext';
import CloseIcon from '../../resources/images/icons-close.svg';
import { AbstractDialog, AbstractDialogProps } from '../AbstractDialog';

export interface DialogAction {
  label: string;
  variant?: 'text' | 'outlined' | 'filled' | 'filled-danger' | 'filled-warning';
  onClick?: () => void;
  disabled?: boolean;
  visible?: boolean;
  primary?: boolean;
  acceptDanger?: boolean;
  acceptWarning?: boolean;
  floatRight?: boolean;
  form?: string;
  type?: string;
  id?: string;
  isLoading?: boolean;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

export interface DialogProps extends AbstractDialogProps {
  open: boolean;
  onClose?: (e?: any, reason?: any) => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  maxHeight?: string | number;
  fullWidth?: boolean;
  fullScreen?: boolean;
  fixedActions?: boolean;
  actions?: DialogAction[];
  justifyActions?: string;
  disabledToBreakpoint?: string;
  topCloseButton?: boolean;
  sx?: SxProps<MuiTheme>;
  closeButtonSx?: SxProps;
  disableEscapeKeyDown?: boolean;
  contentSx?: SxProps<MuiTheme>;
  PaperProps?: React.ComponentProps<typeof MuiDialog>['PaperProps'];
  // Custom Klayo theme (has isBreakpointDown). Note: withTheme also injects MUI theme
  // under the same key — pass this prop explicitly to use Klayo breakpoint helpers.
  theme?: any;
}

class DialogBase extends AbstractDialog<DialogProps> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    topCloseButton: true,
  };

  static Transition = React.forwardRef<unknown, SlideProps>(function Transition(props, ref) {
    return (
      <Slide direction='up' ref={ref} {...props}>
        {props.children}
      </Slide>
    );
  });

  constructor(props: DialogProps) {
    super(props);
  }

  onBackdropClick(e: any) {}

  onClose(e: any, reason: any) {
    const { onClose } = this.props;
    if (reason === 'backdropClick') return;
    if (onClose) onClose(e, reason);
  }

  render() {
    const {
      open,
      theme,
      fixedActions,
      onClose,
      fullWidth,
      className,
      title,
      children,
      maxWidth,
      maxHeight,
      actions,
      justifyActions,
      disabledToBreakpoint,
      topCloseButton,
      sx,
      disableEscapeKeyDown,
      contentSx,
      fullScreen,
      PaperProps,
      closeButtonSx,
    } = this.props;
    const disabled = disabledToBreakpoint && theme?.isBreakpointDown(disabledToBreakpoint);

    return (
      <MuiDialog
        className={'klayo-dialog' + (className ? ' ' + className : '')}
        open={open}
        fullWidth={fullWidth || true}
        fullScreen={fullScreen || theme?.isBreakpointDown('md')}
        onBackdropClick={this.onBackdropClick.bind(this)}
        onClose={this.onClose.bind(this)}
        PaperProps={{
          ...PaperProps,
          sx: [
            {
              borderRadius: {
                xs: '0!important',
                sm: '0!important',
                md: !fullScreen ? '16px!important' : '0!important',
              },
              '& .MuiDialogContent-root': { paddingBottom: 0 },
            },
            sx,
            PaperProps?.sx,
          ] as SxProps<MuiTheme>,
        }}
        scroll={fixedActions ? 'paper' : 'body'}
        maxWidth={maxWidth}
        TransitionComponent={DialogBase.Transition as any}
        disableEscapeKeyDown={disableEscapeKeyDown}
      >
        {title && (
          <DialogTitle
            sx={{
              fontSize: { xs: '18px', md: '24px' },
              lineHeight: { xs: '28px', md: '32px' },
              padding: { xs: '26px', md: '40px 40px 32px 40px' },
              letterSpacing: { xs: '-0.3px', md: '-0.5px' },
            }}
          >
            <Stack direction='row' alignItems='center'>
              <div style={{ flexGrow: 1 }}>{title}</div>

              {topCloseButton && (
                <IconButton
                  onClick={onClose}
                  size='medium'
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    width: '40px',
                    height: '40px',

                    '& svg path[fill="#111827"]': {
                      fill: 'currentColor',
                    },
                    ...closeButtonSx,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          </DialogTitle>
        )}

        <DialogContent
          sx={[
            {
              padding: { xs: '20px', md: '40px' },
              maxHeight: maxHeight ? maxHeight : 'unset',
              fontSize: '16px',
            },
            ...(Array.isArray(contentSx) ? contentSx : contentSx ? [contentSx] : []),
          ]}
        >
          <BreakpointTemplate theme={theme} disabledToBreakpoint={disabledToBreakpoint}>
            {children}
          </BreakpointTemplate>
        </DialogContent>

        {(actions || disabled) && (
          <DialogActions
            sx={{
              padding: { xs: '20px', md: '40px' },
            }}
          >
            <ActionsContainer
              actions={actions}
              justifyActions={justifyActions}
              disabled={disabled}
              onClose={onClose}
            />
          </DialogActions>
        )}
      </MuiDialog>
    );
  }
}

export const Dialog: React.FC<DialogProps> = (props) => {
  const appContext = useContext(AppContext);

  return <DialogBase {...props} theme={appContext.theme} />;
};
